"use client"

import Link from "next/link"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface ActionItem {
  /** React key. Unique per row — for collections one invoice can appear
   * twice, so this is not always the invoice id (see `href`). */
  id: string
  customer: string
  amount: number
  /** ISO 4217 code for `amount`. Formats the row so non-USD invoices are
   * not mis-labelled as `$`. */
  currency: string
  detail: string
  /** Row link target. Defaults to `/invoices/{id}`; pass explicitly when
   * `id` is a composite key rather than the invoice id. */
  href?: string
}

/**
 * A money-first headline for the card (replaces the numeric count badge).
 * Used by the "Paid this week" card: a net-money figure with the payment
 * count as a subtitle. The other cards omit it and keep the count badge.
 */
export interface ActionCardHeadline {
  /** Net amount in the primary currency (major units). May be negative. */
  amount: number
  /** ISO 4217 code for `amount`. */
  currency: string
  /** Count of other currencies present; renders a "+N more" hint when > 0. */
  extraCurrencies: number
  /** Muted subtitle under the headline, e.g. "7 payments". */
  subtitle: string
}

interface ActionCardProps {
  title: string
  count: number
  items: ActionItem[]
  viewAllHref: string
  variant: "overdue" | "viewed" | "paid"
  emptyMessage?: string
  loading?: boolean
  error?: string
  /** When set, show a net-money headline + subtitle instead of the count
   * badge (the count still drives "View all" visibility). */
  headline?: ActionCardHeadline
}

const variantStyles = {
  overdue: {
    countBg: "bg-warning/10 text-warning",
    accentBorder: "border-l-warning",
  },
  viewed: {
    countBg: "bg-info/10 text-info",
    accentBorder: "border-l-info",
  },
  paid: {
    countBg: "bg-success/10 text-success",
    accentBorder: "border-l-success",
  },
}

export function ActionCard({
  title,
  count,
  items,
  viewAllHref,
  variant,
  emptyMessage = "Nothing here",
  loading = false,
  error,
  headline,
}: ActionCardProps) {
  const styles = variantStyles[variant]

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const showSkeleton = loading && items.length === 0 && !error
  const hasError = Boolean(error)

  return (
    <Card className={cn("shadow-card", styles.accentBorder, "border-l-4")}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-h3 text-foreground">{title}</CardTitle>
          {/* The money-headline card carries its figure in the body, so the
              count badge is suppressed to avoid a redundant number. */}
          {!headline && (
            <span className={cn(
              "px-2 py-0.5 rounded text-caption font-semibold",
              styles.countBg
            )}>
              {hasError ? "—" : count}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {!hasError && !showSkeleton && headline && (
          <div className="mb-4">
            <p className="text-h2 text-foreground tabular-nums">
              {formatCurrency(headline.amount, headline.currency)}
              {headline.extraCurrencies > 0 && (
                <span className="ml-2 text-caption font-normal text-muted-foreground">
                  +{headline.extraCurrencies} more{" "}
                  {headline.extraCurrencies === 1 ? "currency" : "currencies"}
                </span>
              )}
            </p>
            <p className="text-caption text-muted-foreground">
              {headline.subtitle}
            </p>
          </div>
        )}
        {hasError ? (
          <p
            role="alert"
            className="text-body text-destructive py-4"
          >
            {error}
          </p>
        ) : showSkeleton ? (
          <div className="space-y-3" aria-hidden="true" data-testid="action-card-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-6 rounded bg-muted animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-body text-muted-foreground py-4">{emptyMessage}</p>
        ) : (
          <div className="space-y-3">
            {items.slice(0, 3).map((item) => (
              <Link
                key={item.id}
                href={item.href ?? `/invoices/${item.id}`}
                className="flex items-center justify-between -mx-2 px-2 py-1 rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div>
                  <p className="text-body font-medium text-foreground">{item.customer}</p>
                  <p className="text-caption text-muted-foreground">{item.detail}</p>
                </div>
                <span className="text-body font-medium text-foreground tabular-nums">
                  {formatCurrency(item.amount, item.currency)}
                </span>
              </Link>
            ))}
          </div>
        )}

        {!hasError && count > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-4 text-primary hover:text-primary-hover"
            asChild
          >
            <Link href={viewAllHref}>View all</Link>
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
